
/**
 * Central API client for the application
 * This file consolidates all API access into a single, type-safe interface
 *
 * IMPORTANT: This is the recommended way to access tRPC endpoints.
 * The legacy trpc implementation in utils/trpc/ directory is deprecated.
 *
 * Usage examples:
 *
 * 1. Query example:
 *    const user = await api.auth.me.query();
 *
 * 2. Mutation example:
 *    const result = await api.auth.login.mutate({ email, password });
 *
 * 3. In React components with useEffect:
 *    useEffect(() => {
 *      async function fetchData() {
 *        try {
 *          const result = await api.search.search.query({ query: "example" });
 *          setData(result);
 *        } catch (error) {
 *          console.error('Error:', error);
 *        }
 *      }
 *      fetchData();
 *    }, []);
 *
 * For more examples and migration guidance, see src/utils/trpc/README.md
 */

import { createTRPCReact, httpBatchLink, createTRPCProxyClient } from '@trpc/react-query';
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { devError } from '@/lib/dev-errors';
import type { AppRouter } from '../../../worker/src/routers';
import superjson from 'superjson';
import { DEFAULT_QUERY_CLIENT_OPTIONS } from '../lib/query-config';

// Import types for re-export
import { SensorType, SensorStatus, EventType, SensorData, EventData } from './trpc/types';

// Re-export types
// Re-export types
export { SensorType, SensorStatus, EventType };

export type { SensorData, EventData };

/**
 * Create React Query client with centralized configuration
 *
 * Uses DEFAULT_QUERY_CLIENT_OPTIONS from query-config.ts which provides:
 * - staleTime: 30 seconds (default for all queries)
 * - cacheTime: 5 minutes (garbage collection time for React Query v4)
 * - refetchOnWindowFocus: false (prevents unnecessary refetches)
 * - retry: 1 (single retry on failure)
 *
 * Individual queries can override these defaults using getQueryConfig() helpers.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) =>
      devError(error, { queryKey: String(query.queryKey) }, 'trpc'),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      devError(error, { mutationKey: String(mutation.options.mutationKey) }, 'trpc'),
  }),
  defaultOptions: DEFAULT_QUERY_CLIENT_OPTIONS,
});

/**
 * Helper to get the base URL for API calls
 */
export const getBaseUrl = () => {
  // Hardcode production URL to ensure correct targeting on Cloudflare Pages
  if (import.meta.env.MODE === 'production') {
    return 'https://api.itms.solutions/trpc';
  }

  // Development: check VITE_API_URL first
  if (import.meta.env.VITE_API_URL) {
    if (import.meta.env.VITE_API_URL.endsWith('/trpc')) {
      return import.meta.env.VITE_API_URL;
    }
    return `${import.meta.env.VITE_API_URL}/trpc`;
  }

  // Default fallback for local development
  if (typeof window !== 'undefined') {
    return 'https://api.itms.solutions/trpc';
  }
  return 'https://api.itms.solutions/trpc';
};

/**
 * Use superjson transformer to match the backend configuration
 * This ensures proper serialization/deserialization of data between client and server
 */
export const transformer = superjson;

/**
 * Main tRPC client for making API calls
 */
export const trpc = createTRPCReact<AppRouter>();
export const api = trpc;

/**
 * Create a tRPC client instance configuration
 */
export const trpcClientConfig = {
  transformer,
  links: [
    httpBatchLink({
      url: getBaseUrl(),
      headers() {
        // Try to get token from localStorage first
        const localStorageToken =
          typeof window !== 'undefined'
            ? localStorage.getItem('smartcity_auth_token') || localStorage.getItem('auth-token')
            : null;

        // Function to get cookie values
        const getCookie = (name: string): string | null => {
          if (typeof document === 'undefined') return null;
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
          return null;
        };

        // Try to get token from cookies
        const cookieToken =
          getCookie('auth-token') ||
          getCookie('smartcity_auth_token') ||
          getCookie('next-auth.session-token') ||
          getCookie('__Secure-next-auth.session-token');

        // Use token from either source
        const token = localStorageToken || cookieToken;

        return {
          Authorization: token ? `Bearer ${token}` : '',
          'x-trpc-source': 'react',
        };
      },
      fetch(url, options) {
        return fetch(url, {
          ...options,
          credentials: 'include',
        });
      },
    }),
  ],
};

// Console log to indicate tRPC client initialization in development
if (import.meta.env.MODE === 'development') {
}

/**
 * Vanilla tRPC client for usage outside of React components
 */
export const trpcClient = createTRPCProxyClient<AppRouter>(trpcClientConfig as any);

export default api;
